import { baseUrl } from '../../shared/utils';

import { type SamlProviderMetadataFileProperties } from './types/saml';
import { resolveNameIdFormat, toNameIdUrn } from './utils/nameIdFormats';
import {
  type SamlProvider,
  type SamlProviderEditableProperties,
} from './types';
import { makeFileFromDataUri } from './utils';

interface EditableConfigType {
  spEntityId?: string;
  wantAssertionsSigned: boolean;
  wantAssertionsEncrypted: boolean;
  forceAuthn: boolean;
  validateSignature: boolean;
  signSpMetadata: boolean;
  nameIdFormat?: string;
  principalType?: string;
  principalAttribute?: string;
}

interface ReadOnlyConfigType {
  singleLogoutServiceUrl?: string;
  authenticationRequestSigned: boolean;
  nameIdFormat?: string;
}

interface SamlProviderResponse {
  displayName?: string;
  name: string;
  protocol: 'saml';
  enabled: boolean;
  testMode?: boolean;
  config: ReadOnlyConfigType & EditableConfigType;
}

interface ImportConfigResponse {
  nameIDPolicyFormat?: string;
  wantAuthnRequestsSigned?: string;
  singleLogoutServiceUrl?: string;
  [key: string]: unknown;
}

/**
 * Imports SAML configuration from a metadata URL via the backend proxy.
 * The backend fetches and parses the remote metadata, avoiding CORS issues.
 */
export async function importSamlConfig(
  metadataUrl: string
): Promise<SamlProviderMetadataFileProperties> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/identity-providers/import-config`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ metadataUrl }),
    }
  );

  if (!response.ok) {
    throw new Error();
  }

  const data = (await response.json()) as ImportConfigResponse;

  // Backend returns short-form NameID (e.g. "EmailAddress").
  // Only resolve/convert when the backend actually provided a value;
  // an absent value means the remote metadata had no NameIDFormat,
  // so we must keep it empty so the UI enables the dropdown.
  const rawNameId = data.nameIDPolicyFormat ?? '';
  const nameIDPolicyFormat = rawNameId
    ? toNameIdUrn(resolveNameIdFormat(rawNameId))
    : '';

  return {
    protocol: 'saml',
    nameIDPolicyFormat,
    wantsAuthRequestsSigned: data.wantAuthnRequestsSigned === 'true',
    logoutServiceUrl: data.singleLogoutServiceUrl ?? '',
  };
}

/** Fetches details for a specific SAML provider by alias.  */
export async function getSamlProviderDetails(
  alias: string
): Promise<SamlProvider> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/identity-providers/${encodeURIComponent(alias)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    throw new Error();
  }

  const data = (await response.json()) as SamlProviderResponse;

  // Normalize nameIdFormat: API may return short form (e.g. "emailAddress")
  // or full URN — resolveNameIdFormat handles casing differences and URN
  // reverse-lookup, then toNameIdUrn ensures we always store the full URN.
  const rawNameId = data.config.nameIdFormat ?? '';
  const nameIdFormatUrn = rawNameId
    ? toNameIdUrn(resolveNameIdFormat(rawNameId))
    : '';

  return {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    name: data.displayName || data.name || '',
    alias,
    protocol: data.protocol,
    isEnabled: !!data.enabled,
    isTestMode: !!data.testMode,
    wantsAuthRequestsSigned: !!data.config.authenticationRequestSigned,
    nameIDPolicyFormat: nameIdFormatUrn,
    nameIdFormat: nameIdFormatUrn,
    spEntityId: data.config.spEntityId ?? '',
    wantsAssertionsSigned: !!data.config.wantAssertionsSigned,
    shouldSignSPMetadata: !!data.config.signSpMetadata,
    wantsAssertionsEncrypted: !!data.config.wantAssertionsEncrypted,
    shouldForceAuthentication: !!data.config.forceAuthn,
    shouldValidateSignatures: !!data.config.validateSignature,
    logoutServiceUrl: data.config.singleLogoutServiceUrl ?? '',
    principalType: data.config.principalType ?? '',
    principalAttribute: data.config.principalAttribute ?? '',
  };
}

interface SamlProviderRequest {
  displayName: string;
  protocol: 'saml';
  enabled: boolean;
  metadataUrl?: string;
  config: EditableConfigType;
}

async function samlProviderUpdate(
  method: 'POST' | 'PUT',
  {
    entityDescriptorUrl,
    nameIdFormat,
    principalType,
    principalAttribute,
    ...provider
  }: SamlProviderEditableProperties
) {
  const formData = new FormData();

  let metadataUrl: undefined | string;

  if (entityDescriptorUrl) {
    if (entityDescriptorUrl.startsWith('data')) {
      formData.append('metadata', makeFileFromDataUri(entityDescriptorUrl));
    } else {
      metadataUrl = entityDescriptorUrl;
    }
  }

  const config: SamlProviderRequest = {
    displayName: provider.name,
    protocol: 'saml',
    enabled: provider.isEnabled,
    metadataUrl,
    config: {
      spEntityId: provider.spEntityId,
      wantAssertionsSigned: provider.wantsAssertionsSigned,
      wantAssertionsEncrypted: provider.wantsAssertionsEncrypted,
      forceAuthn: provider.shouldForceAuthentication,
      validateSignature: provider.shouldValidateSignatures,
      signSpMetadata: provider.shouldSignSPMetadata,
      ...(nameIdFormat ? { nameIdFormat } : {}),
      ...(principalType ? { principalType } : {}),
      ...(principalAttribute ? { principalAttribute } : {}),
    },
  };

  formData.append('config', JSON.stringify(config));

  const response = await fetch(
    `${baseUrl.authms}/v1/sso/identity-providers/${encodeURIComponent(
      provider.alias
    )}`,
    {
      method,
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error();
  }
}

/** Creates a new SAML Provider */
export async function createSamlProvider(
  provider: SamlProviderEditableProperties
): Promise<void> {
  await samlProviderUpdate('POST', provider);
}

/** Updates an existing SAML provider */
export async function updateSamlProvider(
  provider: SamlProviderEditableProperties
): Promise<void> {
  await samlProviderUpdate('PUT', provider);
}

/** Deletes an existing provider */
export async function deleteProvider(alias: string): Promise<void> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/identity-providers/${encodeURIComponent(alias)}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    throw new Error();
  }
}

/** Enables or disables an existing provider. It also disables test mode. */
export async function enableProvider(
  alias: string,
  isEnabled: boolean
): Promise<void> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/identity-providers/${encodeURIComponent(alias)}/${
      isEnabled ? 'enable' : 'disable'
    }`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    throw new Error();
  }
}

/** Enables or disables test mode for an existing provider. If enabling test
 * mode, IdP is also enabled. If disabling test mode, IdP is also disabled. */
export async function setTestMode(
  alias: string,
  isEnabled: boolean
): Promise<void> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/identity-providers/${encodeURIComponent(
      alias
    )}/test-mode/${isEnabled ? 'enable' : 'disable'}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    throw new Error();
  }
}

interface SPMetadataResponse {
  metadataUrl: string;
}

/** Get the SP Metadata URL */
export async function getSpMetadata(alias: string): Promise<string> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/identity-providers/${encodeURIComponent(
      alias
    )}/sp-metadata`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    throw new Error();
  }

  const data = (await response.json()) as SPMetadataResponse;

  return data.metadataUrl;
}

interface GeneralConfigurationResponse {
  usernameMapping: string[];
  multiIDPUserLookup: string;
}

interface GeneralConfiguration {
  usernameMapping: string[];
  multiIdpLookup: string | null;
}

/** Retrieve customer's general SSO settings */
export async function getGeneralConfiguration(): Promise<GeneralConfiguration> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/general-configuration`,
    { headers: { 'Content-Type': 'application/json' } }
  );

  if (!response.ok) {
    throw new Error();
  }

  const data = (await response.json()) as GeneralConfigurationResponse;

  return {
    usernameMapping: data.usernameMapping,
    multiIdpLookup: data.multiIDPUserLookup || null,
  };
}

/** Save customer's general SSO settings */
export async function saveGeneralConfiguration(
  usernameMapping: string[],
  multiIdpLookup: string | null
): Promise<void> {
  const body: Record<string, unknown> = { usernameMapping };

  if (multiIdpLookup) {
    body.multiIDPUserLookup = multiIdpLookup;
  }

  const response = await fetch(
    `${baseUrl.authms}/v1/sso/general-configuration`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error();
  }
}

type SSOKeyResponse = {
  name?: string;
  kid: string;
  priority: number;
  x5c: string[];
}[];

export interface SSOKey {
  name: string;
  kid: string;
  priority: number;
  x5c: string;
}

/** Get customer's custom SSO keys */
export async function getKeys(): Promise<SSOKey[]> {
  const response = await fetch(`${baseUrl.authms}/v1/sso/keys`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error();
  }

  const data = (await response.json()) as SSOKeyResponse;

  return data
    .map((datum) => ({
      kid: datum.kid,
      name: datum.name ?? datum.kid,
      priority: datum.priority,
      x5c: datum.x5c[0],
    }))
    .sort((a, b) => a.priority - b.priority);
}

/** Removes specified key from customer's SSO configuration */
export async function deleteKey(kid: string): Promise<void> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/keys?keyId=${encodeURIComponent(kid)}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    throw new Error();
  }
}

/** Makes the specified certificate the highest priority */
export async function activateKey(kid: string): Promise<void> {
  const response = await fetch(
    `${baseUrl.authms}/v1/auth/certificate/${encodeURIComponent(kid)}/activate`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    throw new Error();
  }
}

/** Adds specified key to customer's SSO configuration */
export async function addKey(
  displayName: string,
  privateKey: string,
  certificate: string
): Promise<void> {
  const response = await fetch(`${baseUrl.authms}/v1/sso/keys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: displayName,
      privateKeyPem: privateKey,
      certificatePem: certificate,
      algorithm: 'RS256',
      keyUse: 'SIG',
    }),
  });

  if (!response.ok) {
    throw new Error();
  }
}
